import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "relative disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      variant: {
        default:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-90 bg-primary text-primary-foreground shadow hover:bg-primary/90 overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        destructive:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-90 bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        outline:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-90 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        secondary:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-90 bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        ghost: 
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-90 hover:bg-accent hover:text-accent-foreground overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        link: 
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-90 text-primary underline-offset-4 hover:underline overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        "wallet-btn": 
          "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors hover:opacity-90 text-white text-xl font-extrabold shadow overflow-hidden [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        bare: 
          "bg-transparent hover:bg-transparent border-none shadow-none p-0 m-0 hover:opacity-100 rounded-lg",
      },
      size: {
        default: "h-9 px-4 py-2",
        walletBtn: 'px-3 py-2',
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        none: "", // No size constraints
      },
      shine: {
        true: "",
        false: "",
      },
      fillOnPress: {
        true: "",
        false: "",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      shine: false,
      fillOnPress: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  shine?: boolean
  shineInterval?: number
  shineDuration?: number
  shineOpacity?: number
  shineWidth?: number
  shineAngle?: number
  bounceEffect?: 'subtle' | 'medium' | 'strong' | 'none'
  bounceTimeout?: number
  fillOnPress?: boolean
  fillColor?: string
  fillDuration?: number
  // New props for debounce
  debounceTime?: number
  disableDebounce?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    shine = false, 
    shineInterval = 100,
    shineDuration = 2000,  
    shineOpacity = 20,     
    shineWidth = 10,
    shineAngle = 45,
    bounceEffect = 'subtle',
    fillOnPress = false,
    fillColor = 'bg-gray-200',
    fillDuration = 200,
    // Debounce props with defaults
    debounceTime = 500,
    disableDebounce = false,
    asChild = false,
    onClick,
    ...props 
  }, ref) => {
    const [animating, setAnimating] = React.useState(false)
    const [isPressed, setIsPressed] = React.useState(false)
    const [fillActive, setFillActive] = React.useState(false)
    // Add state for click prevention
    const [isClickDisabled, setIsClickDisabled] = React.useState(false)
    
    const animationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null)
    const pressTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
    const fillTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
    // Add ref for click debounce timeout
    const clickTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
    
    const Comp = asChild ? Slot : "button"
    
    // Determine if bounce should be applied
    const shouldBounce = bounceEffect !== 'none'
    
    // Animation cycle for shine effect
    const startShineAnimation = React.useCallback(() => {
      setAnimating(true)
      
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
      }
      
      animationTimeoutRef.current = setTimeout(() => {
        setAnimating(false)
        
        if (shine) {
          intervalRef.current = setTimeout(() => {
            startShineAnimation()
          }, shineInterval)
        }
      }, 3000)
    }, [shineInterval, shine])
    
    // Get bounce scale values based on selected effect strength
    const getBounceStyles = () => {
      const scales = {
        subtle: { pressed: 0.97, normal: 1 },
        medium: { pressed: 0.95, normal: 1 },
        strong: { pressed: 0.92, normal: 1 },
        none: { pressed: 1, normal: 1 }  // No scale change for 'none'
      }
      
      return scales[bounceEffect] || scales.subtle
    }
    
    const bounceScales = getBounceStyles()
    
    // Handle click with debounce
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // If debouncing is disabled or button is not in disabled state, allow the click
      if (disableDebounce || !isClickDisabled) {
        // Call original onClick if exists
        onClick?.(e)
        
        // Only apply debounce if it's not disabled
        if (!disableDebounce) {
          // Disable further clicks
          setIsClickDisabled(true)
          
          // Re-enable after debounceTime
          if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current)
          }
          
          clickTimeoutRef.current = setTimeout(() => {
            setIsClickDisabled(false)
          }, debounceTime)
        }
      } else {
        // Prevent the default action if clicks are debounced
        e.preventDefault()
      }
    }
    
    // Start the shine animation cycle
    React.useEffect(() => {
      if (shine) {
        startShineAnimation()
      } else {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current)
          intervalRef.current = null
        }
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current)
          animationTimeoutRef.current = null
        }
        setAnimating(false)
      }
      
      return () => {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current)
          intervalRef.current = null
        }
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current)
          animationTimeoutRef.current = null
        }
      }
    }, [shine, startShineAnimation])

    // Handle press - only trigger bounce state if bounce is enabled
    const handlePress = (e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
      if (shouldBounce) {
        setIsPressed(true)
      }
      
      // Activate fill effect if enabled
      if (fillOnPress) {
        setFillActive(true)
        
        // Auto-reset fill after duration
        if (fillTimeoutRef.current) {
          clearTimeout(fillTimeoutRef.current)
        }
        
        fillTimeoutRef.current = setTimeout(() => {
          setFillActive(false)
        }, fillDuration * 2) // Double the animation time to ensure it completes
      }
      
      // For MouseEvent
      if ('onMouseDown' in props && 'type' in e && e.type === 'mousedown') {
        props.onMouseDown?.(e as React.MouseEvent<HTMLButtonElement>)
      }
      
      // For TouchEvent
      if ('onTouchStart' in props && 'type' in e && e.type === 'touchstart') {
        props.onTouchStart?.(e as React.TouchEvent<HTMLButtonElement>)
      }
      
      // Auto-release safety timer (important for mobile)
      if (pressTimeoutRef.current) {
        clearTimeout(pressTimeoutRef.current)
      }
      
      if (shouldBounce) {
        pressTimeoutRef.current = setTimeout(() => {
          setIsPressed(false)
        }, 1000) // 1 second maximum press time
      }
    }
    
    // Handle release
    const handleRelease = (e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
      if (shouldBounce) {
        setIsPressed(false)
      
        if (pressTimeoutRef.current) {
          clearTimeout(pressTimeoutRef.current)
          pressTimeoutRef.current = null
        }
      }
      
      // For MouseEvent
      if ('onMouseUp' in props && 'type' in e && e.type === 'mouseup') {
        props.onMouseUp?.(e as React.MouseEvent<HTMLButtonElement>)
      }
      
      // For TouchEvent
      if ('onTouchEnd' in props && 'type' in e && e.type === 'touchend') {
        props.onTouchEnd?.(e as React.TouchEvent<HTMLButtonElement>)
      }
    }
    
    // Handle touch/mouse cancel events
    const handleCancel = (e: React.TouchEvent<HTMLButtonElement> | React.MouseEvent<HTMLButtonElement>) => {
      if (shouldBounce) {
        setIsPressed(false)
        
        if (pressTimeoutRef.current) {
          clearTimeout(pressTimeoutRef.current)
          pressTimeoutRef.current = null
        }
      }
      
      // Handle appropriate event based on type
      if ('type' in e) {
        if (e.type === 'touchcancel' && 'onTouchCancel' in props) {
          props.onTouchCancel?.(e as React.TouchEvent<HTMLButtonElement>)
        } else if (e.type === 'mouseleave' && 'onMouseLeave' in props) {
          props.onMouseLeave?.(e as React.MouseEvent<HTMLButtonElement>)
        }
      }
    }
    
    // Clean up all timeouts
    React.useEffect(() => {
      return () => {
        if (pressTimeoutRef.current) {
          clearTimeout(pressTimeoutRef.current)
          pressTimeoutRef.current = null
        }
        if (intervalRef.current) {
          clearTimeout(intervalRef.current)
          intervalRef.current = null
        }
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current)
          animationTimeoutRef.current = null
        }
        if (fillTimeoutRef.current) {
          clearTimeout(fillTimeoutRef.current)
          fillTimeoutRef.current = null
        }
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current)
          clickTimeoutRef.current = null
        }
      }
    }, [])

    // Dynamic styles based on press state
    const buttonScale = isPressed ? bounceScales.pressed : bounceScales.normal
    const buttonTranslateY = isPressed ? (shouldBounce ? 1 : 0) : 0
    
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, shine, className }),
          "transition-transform duration-150"
        )}
        style={{
          transform: `scale(${buttonScale}) translateY(${buttonTranslateY}px)`
        }}
        ref={ref}
        onClick={handleClick}
        onMouseDown={handlePress}
        onMouseUp={handleRelease}
        onMouseLeave={handleCancel}
        onTouchStart={handlePress}
        onTouchEnd={handleRelease}
        onTouchCancel={handleCancel}
        {...props}
      >
        {props.children}
        
        {/* Shine effect */}
        {shine && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              style={{
                position: 'absolute',
                top: '-100%',
                left: '-100%',
                width: '300%',
                height: '300%',
                background: `linear-gradient(${shineAngle}deg, 
                  transparent, 
                  transparent 35%, 
                  rgba(255, 255, 255, ${shineOpacity/100}) ${45 - shineWidth/2}%, 
                  rgba(255, 255, 255, ${shineOpacity/100}) ${45 + shineWidth/2}%, 
                  transparent ${45 + shineWidth + 10}%, 
                  transparent)`,
                transform: `translateX(${animating ? '50%' : '-50%'})`,
                transition: animating ? `transform ${shineDuration}ms cubic-bezier(0.4, 0.0, 0.2, 1)` : 'none',
              }}
            />
          </div>
        )}
        
        {/* Fill on press effect - modified to use opacity fade instead of scaling */}
        {fillOnPress && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ borderRadius: 'inherit' }}>
            <div
              className={`absolute inset-0 ${fillColor}`}
              style={{
                opacity: fillActive ? 0.8 : 0,
                transition: `opacity ${fillDuration}ms ease-out`,
              }}
            />
          </div>
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button }